<template>
  <b-container fluid>
    <admin-breadcrumb name="CTS Driver & Vehicle Upload"/>
    <b-row>
      <b-col cols="12" lg="8">
        <admin-panel
            title="Upload CTS Driver & Vehicle Information"
            sub-title="Upload the latest CTS fleet workbook."
            with-footer
            :second-body="!!importResult"
        >
          <b-alert show variant="warning">
            Drivers and vehicles missing from the workbook will be marked inactive.
          </b-alert>

          <b-form @submit.prevent="submitFleet">
            <b-form-group
                label-for="cts-fleet-file"
                :invalid-feedback="validation.file"
            >
              <template #label>
                Driver & Vehicle Workbook <span class="required-asterisk">*</span>
              </template>
              <b-form-file
                  id="cts-fleet-file"
                  v-model="form.file"
                  :state="fieldState('file')"
                  placeholder="Choose the CTS driver and vehicle workbook or drop it here"
                  drop-placeholder="Drop workbook here"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              />
            </b-form-group>
          </b-form>

          <template #footer>
            <b-btn
                variant="primary"
                :disabled="formState.processing || !form.file"
                @click.prevent="submitFleet"
            >
              Upload Driver & Vehicle Information
              <b-spinner v-if="formState.processing" class="ml-1" small/>
            </b-btn>
          </template>

          <template #second-body>
            <div class="d-flex flex-wrap justify-content-between align-items-center mb-3">
              <div>
                <h5 class="mb-1">Latest Import</h5>
                <p class="text-muted mb-0">Driver and vehicle information imported successfully.</p>
              </div>
              <b-badge variant="success">
                {{ importResult.drivers_imported }} drivers, {{ importResult.vehicles_imported }} vehicles
              </b-badge>
            </div>

            <b-row>
              <b-col sm="6" md="3">
                <div class="summary-label">Drivers Created</div>
                <div class="summary-value">{{ importResult.drivers_created }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Drivers Updated</div>
                <div class="summary-value">{{ importResult.drivers_updated }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Vehicles Created</div>
                <div class="summary-value">{{ importResult.vehicles_created }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Vehicles Updated</div>
                <div class="summary-value">{{ importResult.vehicles_updated }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Drivers Deactivated</div>
                <div class="summary-value">{{ importResult.drivers_deactivated }}</div>
              </b-col>
              <b-col sm="6" md="3">
                <div class="summary-label">Vehicles Deactivated</div>
                <div class="summary-value">{{ importResult.vehicles_deactivated }}</div>
              </b-col>
            </b-row>
          </template>
        </admin-panel>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import AdminBreadcrumb from "../components/AdminBreadcrumb.vue";
import AdminPanel from "../components/AdminPanel.vue";
import {API_ROUTES} from "../api";

export default {
  name: "AdminCtsFleetUpload",
  components: {AdminPanel, AdminBreadcrumb},
  data() {
    return {
      form: {file: null},
      formState: {processing: false},
      validation: {},
      importResult: null
    };
  },
  methods: {
    fieldState(fieldName) {
      return typeof this.validation[fieldName] === 'undefined' ? null : false;
    },
    submitFleet() {
      if (!this.form.file) {
        this.validation = {file: 'Please choose a CTS driver and vehicle workbook.'};
        return;
      }

      const formData = new FormData();
      formData.append('file', this.form.file, this.form.file.name);
      this.formState.processing = true;
      this.validation = {};

      this.$api.post(API_ROUTES.ADMIN_CTS_FLEET_IMPORT, formData, {
        headers: {'Content-Type': 'multipart/form-data'}
      }).then(({data}) => {
        this.importResult = data.summary;
        this.form.file = null;
        this.$bvToast.toast('CTS driver and vehicle information imported successfully.', {
          title: 'Success!',
          variant: 'success'
        });
      }).catch(({response}) => {
        const data = response?.data || {};
        const validationErrors = {};

        if (typeof data.errors !== 'undefined') {
          Object.keys(data.errors).forEach((fieldName) => {
            validationErrors[fieldName] = data.errors[fieldName].join(', ');
          });
        }

        this.validation = validationErrors;
        this.$bvToast.toast(data.message ?? 'Failed to import CTS driver and vehicle information.', {
          title: 'Error!',
          variant: 'danger'
        });
      }).finally(() => {
        this.formState.processing = false;
      });
    }
  }
}
</script>

<style scoped>
.required-asterisk {
  color: #dc3545;
}

.summary-label {
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: uppercase;
}

.summary-value {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
}
</style>
